/**
 * One-time-password challenges.
 *
 * The delivery channel is simulated for now — no SMS gateway is wired up, so
 * the generated code is surfaced in the UI. Everything else (length, expiry,
 * attempt limiting) behaves exactly as the production flow will.
 */
import type { OtpChallenge } from '../types/user';
import { normalizePhone } from './phone';

export const OTP_LENGTH = 6;
/** A code stays valid for five minutes. */
export const OTP_TTL_MS = 5 * 60 * 1000;
export const OTP_MAX_ATTEMPTS = 5;

/** Uniformly distributed 6-digit code (rejection sampling — no modulo bias). */
export function generateOtpCode(): string {
  const ceiling = 10 ** OTP_LENGTH;
  const limit = Math.floor(0xffffffff / ceiling) * ceiling;
  const buf = new Uint32Array(1);
  let value = 0;
  do {
    crypto.getRandomValues(buf);
    value = buf[0];
  } while (value >= limit);
  return String(value % ceiling).padStart(OTP_LENGTH, '0');
}

export function createOtpChallenge(phone: string, now: number = Date.now()): OtpChallenge {
  return {
    id: crypto.randomUUID(),
    phone: normalizePhone(phone),
    code: generateOtpCode(),
    createdAt: new Date(now).toISOString(),
    expiresAt: new Date(now + OTP_TTL_MS).toISOString(),
    attemptsLeft: OTP_MAX_ATTEMPTS,
  };
}

export type OtpFailureReason = 'malformed' | 'expired' | 'locked' | 'mismatch';

export type OtpVerification =
  | { ok: true; challenge: OtpChallenge }
  | { ok: false; reason: OtpFailureReason; challenge: OtpChallenge };

export function isOtpExpired(challenge: OtpChallenge, now: number = Date.now()): boolean {
  return now >= Date.parse(challenge.expiresAt);
}

/** Milliseconds left before the code expires (never negative). */
export function otpTimeLeft(challenge: OtpChallenge, now: number = Date.now()): number {
  return Math.max(0, Date.parse(challenge.expiresAt) - now);
}

/**
 * Verifies an attempt without mutating the input: the returned `challenge`
 * carries the decremented attempt counter and must replace the previous one.
 */
export function verifyOtpChallenge(
  challenge: OtpChallenge,
  input: string,
  now: number = Date.now(),
): OtpVerification {
  if (challenge.attemptsLeft <= 0) return { ok: false, reason: 'locked', challenge };
  if (isOtpExpired(challenge, now)) return { ok: false, reason: 'expired', challenge };

  const code = (input ?? '').replace(/\D+/g, '');
  if (code.length !== OTP_LENGTH) return { ok: false, reason: 'malformed', challenge };

  if (code === challenge.code) return { ok: true, challenge };

  const consumed: OtpChallenge = { ...challenge, attemptsLeft: challenge.attemptsLeft - 1 };
  return {
    ok: false,
    reason: consumed.attemptsLeft <= 0 ? 'locked' : 'mismatch',
    challenge: consumed,
  };
}

export const OTP_ERROR_MESSAGES: Record<OtpFailureReason, string> = {
  malformed: `יש להזין קוד בן ${OTP_LENGTH} ספרות`,
  expired: 'הקוד פג תוקף — שלחו קוד חדש',
  locked: 'מספר הניסיונות מוצה — שלחו קוד חדש',
  mismatch: 'הקוד שגוי',
};
